<template>
  <div ref="wrapperRef" class="m-textarea-wrapper">
    <template v-if="readonly">
      <Textarea ref="textareaRef" :id="uniqueId" :value="props.modelValue" :rows="parsedHeight.rows" :cols="cols" :style="parsedHeight.style" :autoResize="autoResize" :disabled="disabled"
                :readonly="readonly" :placeholder="placeholder" :class="textareaClasses" v-bind="filteredProps"
                @focus="onFocus" @blur="onBlur" />
    </template>
    <FloatLabel v-else variant="on">
      <Textarea ref="textareaRef" :id="uniqueId" v-model="textValue" :rows="parsedHeight.rows" :cols="cols" :style="parsedHeight.style" :autoResize="autoResize" :disabled="disabled"
                :readonly="readonly" :placeholder="placeholder" :class="textareaClasses" v-bind="filteredProps"
                @focus="onFocus" @blur="onBlur" />
      <!--<label :for="uniqueId">{{placeholder}}</label>-->
    </FloatLabel>


    <div v-if="hint && (persistentHint || isFocused)" class="m-textarea-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Textarea from 'primevue/textarea'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { acceptTextFieldFormatInput } from '@/common/utils/textFieldFormat'
import { computed, ref, useAttrs, useId } from 'vue'

interface Props {
  modelValue?: string
  height?: string
  cols?: number
  autoResize?: boolean
  placeholder?: string
  hint?: string
  persistentHint?: boolean
  disabled?: boolean
  readonly?: boolean
  errorMessages?: string | string[]
  platformFormat?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  height: 'auto',
  cols: 20,
  autoResize: false,
  placeholder: '',
  hint: '',
  persistentHint: false,
  disabled: false,
  readonly: false,
  errorMessages: () => [],
  platformFormat: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'blur': []
  'focus': []
  'change': [value: string]
}>()

const attrs = useAttrs()
const isFocused = ref(false)
const uniqueId = useId()
const wrapperRef = ref<HTMLElement | null>(null)
const textareaRef = ref<any>(null)

const filteredProps = computed(() => {
  const { modelValue, placeholder, hint, persistentHint, errorMessages, platformFormat, ...rest } = props
  return { ...rest, ...attrs }
})

const restoreNativeTextareaValue = (previous: string) => {
  const root = textareaRef.value?.$el ?? textareaRef.value
  const el =
    root instanceof HTMLElement
      ? root.matches('textarea')
        ? root
        : (root.querySelector('textarea') as HTMLTextAreaElement | null)
      : null
  if (el) el.value = previous
}

const textValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    const previous = String(props.modelValue ?? '')
    if (!acceptTextFieldFormatInput(props.platformFormat, value)) {
      restoreNativeTextareaValue(previous)
      return
    }
    emit('update:modelValue', value)
  }
})

const textareaClasses = computed(() => [
  'm-textarea',
  {
    'm-textarea--disabled': props.disabled,
    'm-textarea--error': hasErrors.value
  }
])

const parsedHeight = computed(() => {
  const height = props.height || ''

  if (height.endsWith('r')) {
    return { rows: Number(height.slice(0, -1)) || 3, style: undefined }
  }

  const px = parseInt(height)

  if (!isNaN(px)) {
    return { rows: undefined, style: { height: `${px}px` } }
  }

  return { rows: 3, style: { height: 'auto' } }
})

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const hasErrors = computed(() => normalizedErrors.value.length > 0)

const onFocus = () => {
  isFocused.value = true
  emit('focus')
}

const onBlur = () => {
  isFocused.value = false
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-textarea-wrapper {
  position: relative;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  :deep(.p-floatlabel) {
    display: block;
    width: 100%;
    min-width: 0;
  }
}

.m-textarea {
  width: 100%;
  background-color: #ffffff !important;

  :deep(textarea.p-textarea),
  &.p-textarea {
    width: 100%;
    min-height: 80px;
    padding: 0.5rem 0.75rem;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.5;
    border-radius: 4px;
    background: #ffffff !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;
    color: #374151 !important;

    &::placeholder {
      color: rgba(0, 0, 0, 0.38);
    }

    &:focus {
      border-color: var(--primary-color, #1e3a6e) !important;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #1e3a6e) 10%, transparent) !important;
      background: #ffffff !important;
      background-color: #ffffff !important;
    }

    &:disabled {
      opacity: 0.6;
      background: #f9fafb !important;
      background-color: #f9fafb !important;
      color: #374151 !important;
    }
  }

  &--error {
    :deep(textarea.p-textarea),
    &.p-textarea {
      border-color: #e24c4c !important;
    }
  }
}

.m-textarea-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

// Авторесайз
:deep(textarea.p-textarea-resizable) {
  resize: vertical;
}
</style>
